import { TFieldNameMappingItem } from './TFieldNameMappingItem';
import { ProductName } from './TProductUse';

export function getDefaultProject(overwrites: Partial<TProject>): TProject {
  const defaultProject: TProject = {
    id: '',
    isPinned: false,
    name: '',
    keywords: '',
    dataProcessed: false,
    lastProcessDate: '',
    indicationMinDate: '',
    description: '',
    startYear: 0,
    selectedMinYear: 0,
    scores: {
      hcpInfluence: [],
      hcpEngagement: [],
      hcpValue: [],
      center: [],
      centerImpact: [],
      centerPotential: [],
      centerUtil: [],
      clinicalTrials: [],
      risingStar: [],
      hcpProductUse: [],
      risingStarCutoff: 0
    },
    articlesCount: 0,
    regions1: [],
    regions2: [],
    accountRegions: [],
    accountBricks: [],
    regionFiles: [],
    competitors: [],
    treatment_goals: [],
    treatment_options: [],
    treatment_uses: [],
    centersCount: 0,
    hcpsCount: 0,
    tiersDefinitions: {
      center: [],
      centerImpact: [],
      centerPotential: [],
      centerUtil: [],
      clinicalTrials: [],
      hcpEngagement: [],
      hcpInfluence: [],
      hcpValue: [],
      risingStar: [],
      participants: [],
      hcpProductUse: []
    },
    hcpTierFieldDefinitions: [],
    centerTierFieldDefinitions: [],
    hcpTagsDefinitions: [],
    centerTagsDefinitions: [],
    centerFields: [],
    hcpFields: [],
    dynamicFields: [],
    hcpFilter: [],
    centerFilter: [],
    hcpWeight: 1,
    centerWeight: 1,
    CTWeight: 1,
    clinicalTrialsKeywords: [],
    centersDuplicatesRemoved: false,
    hcpDuplicatesRemoved: false,
    hcpIndexes: {},
    centerIndexes: {},
    centerNewsPrompt: ''
  };
  return Object.assign(defaultProject, overwrites);
}
//network pub weight, network CT weight, network inst weight
export type DynamicNumberProjectsField =
  | `networkPubWeight`
  | `networkCtWeight`
  | `networkInstWeight`;

export type DynamicStringProjectsField = `dynamicTestStringField`;

export type DynamicNumberProjectsFieldItem = [DynamicNumberProjectsField, number];
export type DynamicStringProjectsFieldItem = [DynamicStringProjectsField, string];

export type ProjectsDynamicFields = DynamicNumberProjectsFieldItem | DynamicStringProjectsFieldItem;

export type TSalesRegion = {
  id: string;
  name: string;
  description: string;
  salesData: string;
};

export interface TBudget {
  year: number;
  plannedBudget: number;
  actualYearEndBudget: number;
}

export interface TAccountRegion {
  id: string;
  name: string;
  description: string;
  budgets?: TBudget[];
}

export interface TRegionBrick {
  id: string;
  name: string;
  regionId: string;
  sales: TBrickSale[];
  salesCompetitors: TCompetitorSale[];
  salesMomentumShort: number;
  salesMomentumLong: number;
}

export interface TCompetitorSale {
  competitorId: string;
  competitorName: string;
  patientsMomentumShort: number;
  patientsMomentumLong: number;
  // salesMomentumShort: number;
  // salesMomentumLong: number;
  // salesInEuroMomentumShort: number;
  // salesInEuroMomentumLong: number;
  sales: TBrickSale[];
}

export interface TBrickSale {
  year: number;
  month: number;
  sales: number;
  salesInEuro: number;
  patients: number;
}

export interface TProject {
  accountBricks: TRegionBrick[];
  accountRegions: TAccountRegion[];
  articlesCount: number;
  centerFields: TFieldNameMappingItem[];
  centerFilter: TDataFilter[];
  centerIndexes: { [key: string]: number };
  centerNewsPrompt: string;
  centersCount: number;
  centersDuplicatesRemoved: boolean;
  centerTagsDefinitions: TTagDefinition[];
  centerTierFieldDefinitions: TTierFieldDefinition[];
  centerWeight: number;
  clinicalTrialsKeywords: string[];
  competitors: TCompetitor[];
  CTWeight: number;
  dataProcessed: boolean;
  description: string;
  dynamicFields: Array<ProjectsDynamicFields>;
  enableAutoImport?: boolean;
  hcpDuplicatesRemoved: boolean;
  hcpFields: TFieldNameMappingItem[];
  hcpFilter: TDataFilter[];
  hcpIndexes: { [key: string]: number };
  hcpsCount: number;
  hcpTagsDefinitions: TTagDefinition[];
  hcpTierFieldDefinitions: TTierFieldDefinition[];
  hcpWeight: number;
  id: string;
  indicationMinDate: string;
  isPinned: boolean;
  keywords: string;
  lastProcessDate: string;
  name: string;
  regionFiles: TRegionFiles[]
  regions1: TSalesRegion[];
  regions2: TSalesRegion[];
  scores: TScore;
  selectedMinYear: number;
  startYear: number;
  tiersDefinitions: TTiersDefinitions;
  treatment_goals: TTreatment[];
  treatment_options: TTreatment[];
  treatment_uses: TTreatment[];
}

export interface TRegionFiles {
  regionId: string;
  pharmacyDataUrl: string;
  brickDataUrl: string;
  [key: string]: string;
}

export interface TCompetitor {
  id: string;
  name: string;
  description: string;
}
export interface TTagDefinition {
  connectedFieldId: string;
  description: string;
  tags: TTagItem[];
}

export interface TTagItem {
  tag: string;
  name: string;
  value: number;
}

export interface TTierItem {
  position: number;
  name: string;
  low: number;
  high: number;
  total: number;
}

export interface TTiersDefinitions {
  hcpInfluence: TTierItem[];
  hcpEngagement: TTierItem[];
  hcpValue: TTierItem[];
  hcpProductUse: TTierItem[];
  center: TTierItem[];
  centerImpact: TTierItem[];
  centerPotential: TTierItem[];
  centerUtil: TTierItem[];
  clinicalTrials: TTierItem[];
  risingStar: TTierItem[];
  participants: TTierItem[];
}

export interface TTreatment {
  name: string;
  description: string;
}

export interface TScore {
  hcpInfluence: TScoreItem[];
  hcpEngagement: TScoreItem[];
  hcpValue: TScoreItem[];
  center: TScoreItem[];
  centerImpact: TScoreItem[];
  centerPotential: TScoreItem[];
  centerUtil: TScoreItem[];
  clinicalTrials: TScoreItem[];
  risingStar: TScoreItem[];
  hcpProductUse: TProductUseScoreItem[];
  risingStarCutoff: number;
}

export enum Operator {
  Addition,
  Subtraction,
  Multiplication,
  Division,
  BooleanTrue,
  BooleanFalse
}

export interface TProductUseScoreItem {
  area: 'hcpProductUse';
  availableOperators: Operator[];
  operator: Operator;
  score: number | string;
  product: ProductName;
  timePeriod: string;
}

export interface TScoreItem {
  fieldId: string;
  operator: Operator;
  score: number | string;
}

export type NoScoreReport = 'basicHcp' | 'basicCenter' | 'compliance1' | 'metrics';

export interface TNoScoreReport {
  type: NoScoreReport;
  selected: boolean;
}

export type TReportType =
  | 'ValueInfluence'
  | 'ValueEngagement'
  | 'InfluenceEngagement'
  | 'Center'
  | 'RisingStar'
  | 'ProductUseValue'
  | 'ProductUseEngagement'
  | 'ProductUseInfluence';

export type TScoreType =
  | 'hcpInfluence'
  | 'hcpEngagement'
  | 'hcpValue'
  | `hcpProductUse`
  | 'center'
  | 'centerImpact'
  | 'centerPotential'
  | 'centerUtil'
  | 'clinicalTrials'
  | 'risingStar'
  | 'risingStarCutoff';

export interface TTierFieldItem {
  name: string;
  value: number;
}

export interface TTierFieldDefinition {
  fieldId: string;
  tiers: TTierFieldItem[];
}

export enum TReportFilterProperty {
  CONSENT_LEVEL_1 = 'opt_out_information_status',
  CONSENT_LEVEL_2 = 'opt_in_status'
}

export interface TDataFilter {
  property: string;
  operator: FilterOperator;
  value: number | string | boolean;
  type: `engagement` | `regular`;
  tags?: TTagItem[];
  date?: Date;
  metric?: 'year' | 'value';
}

export enum FilterOperator {
  LessOrEqual,
  Less,
  More,
  MoreOrEqual,
  Equal,
  NotEqual
}

export const filterOperatorsMap: Map<FilterOperator, string> = new Map([
  [FilterOperator.Equal, `=`],
  [FilterOperator.Less, `<`],
  [FilterOperator.LessOrEqual, `<=`],
  [FilterOperator.More, `>`],
  [FilterOperator.MoreOrEqual, `>=`],
  [FilterOperator.NotEqual, `!=`]
]);

export function operatorToName(type: Operator): string {
  switch (type) {
    case Operator.Addition:
      return `+`;
    case Operator.BooleanFalse:
      return `False`;
    case Operator.BooleanTrue:
      return `True`;
    case Operator.Division:
      return `/`;
    case Operator.Multiplication:
      return `*`;
    case Operator.Subtraction:
      return `-`;
  }
}
